import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    TablePagination
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    FilterList as FilterListIcon,
    VpnKey as VpnKeyIcon
} from '@mui/icons-material';

import { ContentCircularLoader } from '@libComponents';
import MenuTable from './MenuTable';
import MenuDialog from './MenuDialog';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import PopulatePermissionDialog from './PopulatePermissionDialog';

import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';

const MenuManagement = () => {
    const dispatch = useDispatch();

    // Redux state
    const menus = useSelector(selectors.menusSelector);
    const loading = useSelector(selectors.loadingSelector);
    const total = useSelector(selectors.totalSelector);

    // Local state for filters and pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('menuId');
    const [sortOrder, setSortOrder] = useState('asc');
    const [filterParentId, setFilterParentId] = useState('');
    const [filterGroupId, setFilterGroupId] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Fetch menus and permissions on mount
    useEffect(() => {
        dispatch(actions.fetchMenus());
        dispatch(actions.fetchPermissions());
    }, [dispatch]);

    // Filter and sort menus
    const filteredAndSortedMenus = useMemo(() => {
        let result = [...menus];

        // Search filter
        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            result = result.filter(
                (menu) =>
                    menu.name?.toLowerCase().includes(lowerSearch) ||
                    menu.code?.toLowerCase().includes(lowerSearch) ||
                    menu.menuId?.toString().includes(lowerSearch) ||
                    menu.permissions?.some((p) =>
                        p.name?.toLowerCase().includes(lowerSearch)
                    )
            );
        }

        // Parent ID filter
        if (filterParentId !== '') {
            result = result.filter(
                (menu) => menu.parentId === parseInt(filterParentId)
            );
        }

        // Group ID filter
        if (filterGroupId !== '') {
            result = result.filter(
                (menu) => menu.groupId === parseInt(filterGroupId)
            );
        }

        // Sort
        result.sort((a, b) => {
            let aValue = a[sortBy];
            let bValue = b[sortBy];

            // Handle null/undefined
            if (aValue == null) aValue = '';
            if (bValue == null) bValue = '';

            // Convert to string for comparison
            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (sortOrder === 'asc') {
                return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
            } else {
                return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
            }
        });

        return result;
    }, [menus, searchTerm, sortBy, sortOrder, filterParentId, filterGroupId]);

    // Get unique parent IDs and group IDs for filters
    const uniqueParentIds = useMemo(() => {
        return [...new Set(menus.map((m) => m.parentId))].sort((a, b) => a - b);
    }, [menus]);

    const uniqueGroupIds = useMemo(() => {
        return [...new Set(menus.map((m) => m.groupId))].sort((a, b) => a - b);
    }, [menus]);

    // Local state for Populate Permission dialog
    const [populatePermissionOpen, setPopulatePermissionOpen] = useState(false);

    // Handlers
    const handleCreateMenu = useCallback(() => {
        dispatch(actions.openMenuDialog(null));
    }, [dispatch]);

    const handleOpenPopulatePermission = useCallback(() => {
        setPopulatePermissionOpen(true);
    }, []);

    const handleClosePopulatePermission = useCallback(() => {
        setPopulatePermissionOpen(false);
    }, []);

    const handleEditMenu = useCallback(
        (menu) => {
            dispatch(actions.openMenuDialog(menu));
        },
        [dispatch]
    );

    const handleDeleteMenu = useCallback(
        (menuId) => {
            dispatch(actions.openDeleteDialog(menuId));
        },
        [dispatch]
    );

    const handleSortChange = useCallback((field) => {
        setSortBy(field);
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    }, []);

    const handleClearFilters = useCallback(() => {
        setSearchTerm('');
        setFilterParentId('');
        setFilterGroupId('');
        setSortBy('menuId');
        setSortOrder('asc');
        setPage(0);
    }, []);

    const handleChangePage = useCallback((event, newPage) => {
        setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);

    // Paginated menus
    const paginatedMenus = useMemo(() => {
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return filteredAndSortedMenus.slice(startIndex, endIndex);
    }, [filteredAndSortedMenus, page, rowsPerPage]);

    if (loading && menus.length === 0) {
        return <ContentCircularLoader />;
    }

    return (
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    mb: 3,
                    gap: 1.5,
                    flexDirection: { xs: 'column', sm: 'row' }
                }}
            >
                <Typography variant="h4" component="h1" sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                    Menu Management
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' } }}>
                    <Button
                        variant="outlined"
                        startIcon={<VpnKeyIcon />}
                        onClick={handleOpenPopulatePermission}
                        size="large"
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                        Populate Permission
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreateMenu}
                        size="large"
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                        Create Menu
                    </Button>
                </Box>
            </Box>

            {/* Filters and Search */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        '& > *': {
                            minWidth: { xs: '100%', sm: 'auto' }
                        }
                    }}
                >
                    {/* Search */}
                    <TextField
                        placeholder="Search by name, code, ID, or permissions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="small"
                        sx={{ 
                            flex: '1 1 300px', 
                            minWidth: 250,
                            '& .MuiOutlinedInput-root': {
                                height: '36px'
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />

                    {/* Parent ID Filter */}
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Parent ID</InputLabel>
                        <Select
                            value={filterParentId}
                            label="Parent ID"
                            onChange={(e) => setFilterParentId(e.target.value)}
                            sx={{ height: '36px' }}
                        >
                            <MenuItem value="">All</MenuItem>
                            {uniqueParentIds.map((id) => (
                                <MenuItem key={id} value={id}>
                                    {id}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Group ID Filter */}
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Group ID</InputLabel>
                        <Select
                            value={filterGroupId}
                            label="Group ID"
                            onChange={(e) => setFilterGroupId(e.target.value)}
                            sx={{ height: '36px' }}
                        >
                            <MenuItem value="">All</MenuItem>
                            {uniqueGroupIds.map((id) => (
                                <MenuItem key={id} value={id}>
                                    {id}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Clear Filters */}
                    <Button
                        variant="outlined"
                        onClick={handleClearFilters}
                        startIcon={<FilterListIcon />}
                        size="small"
                        sx={{ height: '36px' }}
                    >
                        Clear
                    </Button>
                </Box>

                {/* Results count */}
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing {paginatedMenus.length} of {filteredAndSortedMenus.length} filtered menus (Total: {total})
                    </Typography>
                </Box>
            </Paper>

            {/* Table */}
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <MenuTable
                    menus={paginatedMenus}
                    onEdit={handleEditMenu}
                    onDelete={handleDeleteMenu}
                    onSort={handleSortChange}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    loading={loading}
                />
                
                {/* Pagination */}
                <TablePagination
                    component="div"
                    count={filteredAndSortedMenus.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    labelRowsPerPage="Rows per page:"
                    sx={{
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        '& .MuiTablePagination-toolbar': {
                            justifyContent: 'space-between',
                            paddingLeft: 2,
                            paddingRight: 2
                        },
                        '& .MuiTablePagination-spacer': {
                            display: 'none'
                        },
                        '& .MuiTablePagination-selectLabel': {
                            margin: 0
                        },
                        '& .MuiTablePagination-displayedRows': {
                            margin: 0,
                            marginLeft: 'auto'
                        }
                    }}
                />
            </Paper>

            {/* Dialogs */}
            <MenuDialog />
            <DeleteConfirmationDialog />
            <PopulatePermissionDialog
                open={populatePermissionOpen}
                onClose={handleClosePopulatePermission}
            />
        </Box>
    );
};

export default MenuManagement;
